import axios from 'axios'
import { Preferences } from '@capacitor/preferences'
import * as types from '../mutation-types'
import { captureException } from "@sentry/browser"

async function getPreference (key) {
  const result = await Preferences.get({ key });
  return result.value ? JSON.parse(result.value) : null;
}

async function setPreference (key, value) {
  return await Preferences.set({ key, value: JSON.stringify(value) });
}

// state
export const state = {
  onboarding_form: false,
  nutrition_onboarding: false,
  period_onboarding: false,
  subscription_portal: false,
  isLoading: true, // Add loading state
  isInitialized: false // Track if data has been loaded from preferences
}

// Load from preferences on initialization
getPreference('onboarding_steps').then((onboardingSteps) => {
  if (onboardingSteps) {
    state.onboarding_form = onboardingSteps.onboarding_form ?? false
    state.nutrition_onboarding = onboardingSteps.nutrition_onboarding ?? false
    state.period_onboarding = onboardingSteps.period_onboarding ?? false
    state.subscription_portal = onboardingSteps.subscription_portal ?? false
  }
  state.isLoading = false
  state.isInitialized = true
})

// getters
export const getters = {
  // If user is signed in, automatically consider onboarding_form as completed
  onboarding_form: (state, getters, rootState, rootGetters) => {
    // If user is authenticated, they must have completed onboarding_form
    if (rootGetters && rootGetters['auth/check']) {
      return true
    }
    return state.onboarding_form
  },
  nutrition_onboarding: state => state.nutrition_onboarding,
  period_onboarding: state => state.period_onboarding,
  subscription_portal: state => state.subscription_portal,
  
  // Loading states
  isLoading: state => state.isLoading,
  isInitialized: state => state.isInitialized,
  
  // General onboarding completion (using stored flag)
  // If user is signed in, automatically consider onboarding_form as completed
  generalOnboardingCompleted: (state, getters, rootState, rootGetters) => {
    // If user is authenticated, they must have completed onboarding_form
    if (rootGetters && rootGetters['auth/check']) {
      return true
    }
    return state.onboarding_form
  },
  
  // Nutrition onboarding completion (using stored flag)
  nutritionOnboardingCompleted: state => state.nutrition_onboarding,
  
  // Period onboarding completion (using stored flag)
  periodOnboardingCompleted: state => state.period_onboarding,
  
  // Overall onboarding completion
  onboardingCompleted: (state, getters) => {
    // Don't return true until data is initialized
    if (!state.isInitialized) return false
    return getters.generalOnboardingCompleted && 
           getters.nutritionOnboardingCompleted && 
           getters.periodOnboardingCompleted
  },
  
  // Should show onboarding progress
  shouldShowOnboardingProgress: (state, getters) => {
    // Don't show onboarding progress until data is loaded
    if (!state.isInitialized) return false
    return !getters.onboardingCompleted
  },
  
  // Completed sections count
  completedOnboardingSections: (state, getters) => {
    // Don't count sections until data is initialized
    if (!state.isInitialized) return 0
    let completed = 0
    if (getters.generalOnboardingCompleted) completed++
    if (getters.nutritionOnboardingCompleted) completed++
    if (getters.periodOnboardingCompleted) completed++
    return completed
  }
}

// mutations
export const mutations = {
  [types.SET_ONBOARDING_STEPS] (state, onboardingSteps) {
    state.onboarding_form = onboardingSteps.onboarding_form ?? false
    state.nutrition_onboarding = onboardingSteps.nutrition_onboarding ?? false
    state.period_onboarding = onboardingSteps.period_onboarding ?? false
    state.subscription_portal = onboardingSteps.subscription_portal ?? false
    state.isLoading = false
    state.isInitialized = true
  },

  [types.UPDATE_ONBOARDING_STEP] (state, { step, value }) {
    state[step] = value
  },

  SET_ONBOARDING_LOADING (state, isLoading) {
    state.isLoading = isLoading
  }
}

// actions
export const actions = {
  async fetchOnboardingSteps ({ commit }) {
    commit('SET_ONBOARDING_LOADING', true)
    
    try {
      const { data } = await axios.get('/onboarding')
      const onboardingSteps = data || {}
      
      commit(types.SET_ONBOARDING_STEPS, onboardingSteps)
      await setPreference('onboarding_steps', onboardingSteps)
      
      return onboardingSteps
    } catch (error) {
      captureException(error)
      console.error('Failed to fetch onboarding steps:', error)
      // Return default values on error
      const defaultSteps = {
        onboarding_form: false,
        nutrition_onboarding: false,
        period_onboarding: false,
        subscription_portal: false
      }
      commit(types.SET_ONBOARDING_STEPS, defaultSteps)
      return defaultSteps
    }
  },

  async updateOnboardingStep ({ commit }, { step, value }) {
    try {
      // Update backend
      await axios.post('/onboarding', { [step]: value })
      
      // Update local state immediately for real-time feedback
      commit(types.UPDATE_ONBOARDING_STEP, { step, value })
      
      // Update preferences
      const currentSteps = await getPreference('onboarding_steps') || {}
      currentSteps[step] = value
      await setPreference('onboarding_steps', currentSteps)
      
      return true
    } catch (error) {
      captureException(error)
      console.error('Failed to update onboarding step:', error)
      throw error
    }
  },

  async updateMultipleSteps ({ commit }, steps) {
    try {
      // Update backend with multiple steps
      await axios.post('/onboarding', steps)
      
      // Update local state for each step
      Object.entries(steps).forEach(([step, value]) => {
        commit(types.UPDATE_ONBOARDING_STEP, { step, value })
      })
      
      // Update preferences
      const currentSteps = await getPreference('onboarding_steps') || {}
      Object.assign(currentSteps, steps)
      await setPreference('onboarding_steps', currentSteps)
      
      return true
    } catch (error) {
      captureException(error)
      console.error('Failed to update onboarding steps:', error)
      throw error
    }
  }
}
